import React from 'react';
import DashboardCard from './DashboardCard';

export interface FunnelStage {
  key: string;
  label: string;
  value: number;
  displayValue: string;
  /** Optional subline, e.g. lead-stage hot count */
  detail?: string;
}

export interface FunnelOverviewCardProps {
  stages: FunnelStage[];
  /** Percentage between stage i and i+1 (same length as stages - 1); null if not computable */
  stepRates: (number | null)[];
  title?: string;
  subtitle?: string;
  /** Optional footer (e.g. revenue strip) inside the same widget */
  footer?: React.ReactNode;
  /** Thin strip when prospects/clients are zero but leads exist */
  variant?: 'default' | 'compact';
  /** Grid span / layout classes only. */
  className?: string;
}

function barPct(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, (value / max) * 100);
}

const FunnelOverviewCard: React.FC<FunnelOverviewCardProps> = ({
  stages,
  stepRates,
  title = 'Pipeline overview',
  subtitle = 'Leads → prospects → clients. Bar width is relative to your largest stage.',
  footer,
  variant = 'default',
  className,
}) => {
  const numericValues = stages.map((s) => s.value);
  const max = Math.max(...numericValues, 1);

  if (variant === 'compact') {
    const summary = stages.map((s) => `${s.displayValue} ${s.label.toLowerCase()}`).join(' · ');
    return (
      <DashboardCard
        title={title}
        action={footer ? <div className="text-sm text-fg-muted">{footer}</div> : undefined}
        className={className}
      >
        <p className="text-sm text-fg-muted tabular-nums">{summary}</p>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard title={title} subtitle={subtitle} footer={footer} className={className}>
      <div className="space-y-5">
        {stages.map((stage, i) => (
          <div key={stage.key}>
            <div className="flex justify-between items-baseline gap-2 mb-1.5">
              <span className="text-sm font-medium text-fg-secondary">{stage.label}</span>
              <span className="text-sm font-semibold text-fg tabular-nums">
                {stage.displayValue}
              </span>
            </div>
            {stage.detail ? (
              <p className="text-xs text-fg-muted mb-1.5">{stage.detail}</p>
            ) : null}
            <div className="h-2.5 rounded-full bg-app border border-border/60 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${barPct(stage.value, max)}%` }}
              />
            </div>
            {i < stages.length - 1 ? (
              <p className="text-xs text-fg-muted mt-1.5">
                {stepRates[i] != null
                  ? `Next step: ${stepRates[i]!.toFixed(1)}%`
                  : i === stages.length - 2
                    ? 'Booked revenue—see activity below for sources.'
                    : 'Not enough data yet'}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </DashboardCard>
  );
};

export default FunnelOverviewCard;
